"use client";

import { Modal } from "@/components/ui/modal";
import { PhoneStep } from "./phone-step";
import { OtpStep } from "./otp-step";
import { RegisterStep } from "./register-step";
import { useAuthFlow } from "@/hooks/use-auth-flow";
import Image from "next/image";
import { X } from "lucide-react";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function AuthModal({ isOpen, onClose, onSuccess }: AuthModalProps) {
  const {
    step,
    phone,
    sendOtp,
    verifyOtp,
    register,
    handleSendOtp,
    handleVerifyOtp,
    handleRegister,
    reset,
  } = useAuthFlow(() => {
    onSuccess?.();
    onClose();
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Image
              src="/fast-logo.svg"
              alt="Fast"
              width={64}
              height={28}
              className="h-7 w-auto"
            />
          </div>
          <button
            onClick={handleClose}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100 dark:bg-[#313131] dark:hover:bg-[#313131] dark:text-gray-400 transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <h1 className="text-2xl font-bold text-headline mb-6">
          {step === "register" ? "Inscription" : "Connexion ou inscription"}
        </h1>

        {/* Steps */}
        {step === "phone" && (
          <PhoneStep
            onSubmit={handleSendOtp}
            isPending={sendOtp.isPending}
            error={sendOtp.error}
          />
        )}

        {step === "otp" && (
          <OtpStep
            phone={phone}
            onSubmit={handleVerifyOtp}
            onResend={() => handleSendOtp(phone)}
            onBack={() => reset()}
            isPending={verifyOtp.isPending}
            error={verifyOtp.error}
          />
        )}

        {step === "register" && (
          <RegisterStep
            onSubmit={handleRegister}
            isPending={register.isPending}
            error={register.error}
          />
        )}

        {/* Footer */}
        {step === "phone" && (
          <p className="mt-6 text-center text-xs text-body leading-relaxed">
            En appuyant sur «&nbsp;Continuer&nbsp;», vous acceptez les{" "}
            <a href="https://cgu.fastapp.dz" target="_blank" rel="noopener noreferrer" className="text-primary underline">
              Conditions
            </a>{" "}
            et la{" "}
            <a href="https://privacy.fastapp.dz" target="_blank" rel="noopener noreferrer" className="text-primary underline">
              Politique de confidentialité
            </a>{" "}
            de Fast. Un code de vérification peut vous être envoyé.
          </p>
        )}
      </div>
    </Modal>
  );
}
